/*
 * Copyright (c) 2020. American Express Company
 *
 * FOR INTERNAL USE ONLY. This is not secure enough to use for production.
 * It is a TEMPORARY authentication solution for TESTING PURPOSES ONLY
 */

import _ from 'lodash';
import { ALLOW_TEST_LOGIN, BW_PHANT_URL } from '../config';
import BWDataService from './bw-data.service';
import { FullUserModel, PlanType } from 'src/types/user.model';

import MixpanelService from './mixpanel.service';
import LogoutService from 'src/services/logout.service';

export interface SignupPayload {
    /** The type of plan to create - e.g. "full" or "budgeting" */
    planType: PlanType;
    /** The signup origin for analytics */
    userOrigin?: String;
    /** The terms & conditions version that the user has agreed to */
    termsVersion: number;
}

export default class AuthService extends BWDataService {
    public static override async logoutUser(): Promise<void> {
        MixpanelService.logout();
        await super.logoutUser();
    }

    public static async loginTestUser(email: string): Promise<FullUserModel> {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: email
        };

        return await fetch(BW_PHANT_URL + '/login', requestOptions).then(this.handleResponse);
    }

    public static async signupWithEmail(email: string): Promise<FullUserModel> {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: email
        };

        return await fetch(BW_PHANT_URL + '/signup', requestOptions).then(this.handleResponse);
    }

    public static async login(): Promise<void> {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include' as const
        };

        return await fetch(BW_PHANT_URL + '/login/amex', requestOptions).then(this.handleResponse);
    }

    /**
     * This signs up the new (eligible) user and logs them in.
     * Should not be used in test login environments.
     * @param signupPayload
     */
    public static async signup(signupPayload: SignupPayload): Promise<any> {
        if (ALLOW_TEST_LOGIN) {
            console.error('Eligible users are not supported in test login environments');
            LogoutService.handleLogoutRedirect();
            return;
        }

        const baseRequestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include' as const
        };
        const signupRequestOptions = {
            ...baseRequestOptions,
            body: JSON.stringify(signupPayload)
        };

        await fetch(BW_PHANT_URL + '/signup/amex', signupRequestOptions).then(this.handleResponse);
    }

    /** Create a temporary user from a snapshot (like demo or blank) */
    public static async createTempUser(params: any): Promise<any> {
        // guid is always present, so more than 1 parameter indicates using the POST endpoint,
        // which no longer exists.
        if (_.size(params) > 1) {
            console.log(
                'Error: We no longer have a Buy New Home wordpress experience. Ignoring extra user start' +
                    ' parameters.'
            );
        }
        // only GUID was given, so use the basic snapshot endpoint
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        };
        return await fetch(`${BW_PHANT_URL}/auth/temp/${params.guid}`, requestOptions).then(this.handleResponse);
    }
}
